import WarningIcon from '@mui/icons-material/Warning';
import { LoadingButton } from '@mui/lab';
import { Checkbox, FormControlLabel, Tab, Tabs, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import { JsonEditor } from 'json-edit-react';
import React, { FormEvent, useEffect, useState } from 'react';
import { API } from '../../../../api/api';
import { URLS } from '../../../../api/urls';
import { UploadImage } from '../../../../components/UploadImage/UploadImage';
import { useAlert } from '../../../../context/Alert.context';
import { GadgetDto } from '../../../../types/gadgets.types';
import { ModelDto } from '../../../../types/models.types';
import { ServiceInfo } from '../../../../types/serviceInfo.types';
import { Service } from '../../../../types/services.types';
import { ACTIVE_TAB } from '../../../BlogPage/components/PostForm/consts';
import { chooseLangAccordingToTab } from '../../../ServicesPage/components/useServiceInfoForm';
import {
  chooseFaqKeyAccordingToTab,
  isServiceHubFaqArray,
} from '../../../ServicesPage/modals/ServiceHub/utils/utils';
import { ModelEdit } from '../../GadgetsPage.type';
import styles from './ModelModal.module.css';
import { Services } from './Services/Services';

interface ModelFormProps {
  onSubmit: (model: ModelEdit) => void;
  onCancel: () => void;
  defaultValue?: ModelDto;
  loading: boolean;
  gadgetSlug: string;
}

const empty = {
  nameEn: '',
  nameRu: '',
  namePl: '',
  nameUk: '',
  subtitleEn: '',
  subtitleRu: '',
  subtitlePl: '',
  subtitleUk: '',
  seoTitleEn: '',
  seoTitleRu: '',
  seoTitlePl: '',
  seoTitleUk: '',
  seoDescriptionEn: '',
  seoDescriptionRu: '',
  seoDescriptionPl: '',
  seoDescriptionUk: '',
  seoTitleH1En: '',
  seoTitleH1Ru: '',
  seoTitleH1Pl: '',
  seoTitleH1Uk: '',
  faqPl: [],
  faqEn: [],
  faqUk: [],
  faqRu: [],
  isPopularInMenu: false,
  menuPriority: 0,
};

export const ModelForm = ({
  onSubmit,
  defaultValue,
  onCancel,
  loading,
  gadgetSlug,
}: ModelFormProps) => {
  const { showAlert } = useAlert();
  const [data, setData] = useState(() => ({
    ...empty,
    ...(defaultValue ?? {}),
  }));
  const [slug, setSlug] = useState(defaultValue?.slug || '');
  const [file, setFile] = useState<File | null>(null);
  const [fileHero, setFileHero] = useState<File | null>(null);
  const [serviceInfo, setServiceInfo] = useState<ServiceInfo[]>([]);
  const [services, setServices] = useState<Service[]>(
    defaultValue?.services || [],
  );
  const [previewUrl, setPreviewUrl] = useState(
    defaultValue?.file?.fileName
      ? `${process.env.REACT_APP_API_URL_STATIC}/models/${defaultValue.file.fileName}`
      : null,
  );
  const [previewHeroUrl, setPreviewHeroUrl] = useState(
    defaultValue?.fileHero?.fileName
      ? `${process.env.REACT_APP_API_URL_STATIC}/modelsHero/${defaultValue.fileHero.fileName}`
      : null,
  );
  const [activeTabIndex, setActiveTabIndex] = useState(ACTIVE_TAB.POLISH);

  const name = `name${chooseLangAccordingToTab(activeTabIndex)}` as
    | 'nameEn'
    | 'nameRu'
    | 'nameUk'
    | 'namePl';
  const subtitle = `subtitle${chooseLangAccordingToTab(activeTabIndex)}` as
    | 'subtitleEn'
    | 'subtitleRu'
    | 'subtitleUk'
    | 'subtitlePl';
  const seoTitle = `seoTitle${chooseLangAccordingToTab(activeTabIndex)}` as
    | 'seoTitleEn'
    | 'seoTitleRu'
    | 'seoTitleUk'
    | 'seoTitlePl';
  const seoDescription =
    `seoDescription${chooseLangAccordingToTab(activeTabIndex)}` as
      | 'seoDescriptionEn'
      | 'seoDescriptionRu'
      | 'seoDescriptionUk'
      | 'seoDescriptionPl';
  const seoTitleH1 = `seoTitleH1${chooseLangAccordingToTab(activeTabIndex)}` as
    | 'seoTitleH1En'
    | 'seoTitleH1Ru'
    | 'seoTitleH1Uk'
    | 'seoTitleH1Pl';
  const faqKey = chooseFaqKeyAccordingToTab(activeTabIndex);
  useEffect(() => {
    API.get<GadgetDto>(
      `${URLS.GADGETS}/${gadgetSlug}?serviceInfo=true&multiLanguageMode=true`,
    )
      .then(({ data }) => {
        setServiceInfo(data?.serviceInfo || []);
      })
      .catch((e) => showAlert({ message: e.message, severity: 'error' }));
  }, []);

  const onSubmitHdl = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitBtnDisabled) return;
    onSubmit({
      ...data,
      services,
      file,
      fileHero,
      slug,
    });
  };

  const isSubmitBtnDisabled =
    !data.nameEn?.trim() ||
    !data.nameRu?.trim() ||
    !data.namePl?.trim() ||
    !data.nameUk?.trim() ||
    (!file && !defaultValue?.file) ||
    !services.length ||
    !slug.trim();

  const addService = (service: Service) => {
    const isExist = services.filter(
      (serviceItem) => service.serviceInfoId === serviceItem.serviceInfoId,
    )[0];
    if (isExist) {
      showAlert({
        message: 'The service has already been created',
        severity: 'warning',
      });
      return;
    }
    const newServices = [service, ...services];
    setServices(newServices);
  };

  const update = (value: any, key: any) => {
    setData((prev) => {
      return { ...prev, [key]: value };
    });
  };

  const removeService = (id: string) => {
    const newServices = services.filter((service) => service.id !== id);
    setServices(newServices);
  };

  const editService = (editedService: Service) => {
    const newServices = services.map((service) => {
      if (service.id === editedService.id) {
        return {
          ...service,
          ...editedService,
        };
      }
      return service;
    });
    setServices(newServices);
  };

  if (!serviceInfo.length) {
    return (
      <div>
        <Typography variant="h4">
          Please, create service Info fot this gadget.
        </Typography>
      </div>
    );
  }

  const onTabClick = (_: React.SyntheticEvent, nextIndex: number) => {
    setActiveTabIndex(nextIndex);
  };
  return (
    <form className={styles.form} onSubmit={onSubmitHdl} noValidate>
      <div className={styles.slug}>
        <WarningIcon />
        <TextField
          className={styles.fullWidth}
          name="slug"
          label="Slug"
          value={slug}
          variant="standard"
          required
          onChange={(e) => setSlug(e.target.value)}
        />
      </div>
      <div className={styles.menuSettings}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(data.isPopularInMenu)}
              onChange={(e) => update(e.target.checked, 'isPopularInMenu')}
            />
          }
          label="Popular model in mega-menu"
        />
        <TextField
          className={styles.priorityInput}
          name="menuPriority"
          label="Mega-menu priority"
          type="number"
          value={data.menuPriority ?? 0}
          variant="standard"
          helperText="Higher value appears first"
          onChange={(e) =>
            update(Number(e.target.value) || 0, 'menuPriority')
          }
        />
      </div>
      <div>
        <p>Image for hub page(hero)</p>
        <UploadImage
          isDeleteBtnShouldBe={false}
          name="fileHero"
          previewUrl={previewHeroUrl || ''}
          fileName={fileHero?.name || defaultValue?.fileHero?.fileName || ''}
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              setFileHero(e.target.files[0]);
              const objectUrl = URL.createObjectURL(e.target.files[0]);
              setPreviewHeroUrl(objectUrl);
            }
          }}
        />
      </div>
      <div>
        <p>Image for service page</p>
        <UploadImage
          name="file"
          isDeleteBtnShouldBe={false}
          previewUrl={previewUrl || ''}
          fileName={file?.name || defaultValue?.file?.fileName || ''}
          required
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              setFile(e.target.files[0]);
              const objectUrl = URL.createObjectURL(e.target.files[0]);
              setPreviewUrl(objectUrl);
            }
          }}
        />
      </div>
      <div className={styles.tabContainer}>
        <Tabs value={activeTabIndex} onChange={onTabClick}>
          <Tab label={<TabLabel label="Polish" />} />
          <Tab label={<TabLabel label="English" />} />
          <Tab label={<TabLabel label="Russian" />} />
          <Tab label={<TabLabel label="Ukranian" />} />
        </Tabs>
      </div>
      <TextField
        key={name}
        required
        name="name"
        label="Model name"
        value={data[name]}
        variant="standard"
        onChange={(e) => update(e.target.value, name)}
      />
      <TextField
        key={subtitle}
        name="subtitle"
        label="Model subtitle"
        value={data[subtitle]}
        variant="standard"
        onChange={(e) => update(e.target.value, subtitle)}
      />
      <TextField
        key={seoTitle}
        name="seoTitle"
        label="Model SEO Title"
        value={data[seoTitle]}
        variant="standard"
        onChange={(e) => update(e.target.value, seoTitle)}
      />
      <TextField
        key={seoDescription}
        name="seoDescription"
        label="Model SEO Description"
        value={data[seoDescription]}
        variant="standard"
        onChange={(e) => update(e.target.value, seoDescription)}
      />
      <TextField
        key={seoTitleH1}
        name="seoTitleH1"
        label="Model H1"
        value={data[seoTitleH1]}
        variant="standard"
        onChange={(e) => update(e.target.value, seoTitleH1)}
      />
      <div className={styles.jsonEditor}>
        <p>FAQ Section</p>
        <JsonEditor
          data={data[faqKey]}
          setData={(next) => {
            if (!isServiceHubFaqArray(next)) return;
            setData((prev) => ({
              ...prev,
              [faqKey]: next,
            }));
          }}
        />
      </div>
      {!!serviceInfo.length && (
        <Services
          serviceInfo={serviceInfo}
          onDelete={removeService}
          onAdd={addService}
          onEdit={editService}
          services={services}
        />
      )}
      <DialogActions>
        <Button disabled={loading} variant="outlined" onClick={onCancel}>
          CANCEL
        </Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="contained"
          disabled={isSubmitBtnDisabled}
        >
          SAVE
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

const TabLabel = ({ label }: { label: string }) => {
  return (
    <div className={styles.tabItem}>
      <Typography variant="body1">{label}</Typography>
    </div>
  );
};
